import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAlerts } from "src/hooks/useAlerts";
import { useSimpleDiscountTimerBar } from "./useDiscountTimerBar";
import { AlertBar } from "src/types/graphql.d";
import { getStorageJSON, setStorageJSON } from "src/utils/helper/localStorage";
import { compareAlerts } from "src/utils/helper/alerts";
import { useShopTypeContext } from "./context/use-shop-type-context";
import { useNewIsLoggedIn } from "./useIsLoggedIn";

type RibbonState = {
  showRibbon: boolean;
  toggleShowRibbon: () => void;
  combinedAlertBars: AlertBar[];
  discountMessage: AlertBar | undefined;
};

export const RIBBON_STORAGE_KEY = "ot_alertbar_status";

function useRibbon(): RibbonState {
  const { isLoggedIn } = useNewIsLoggedIn();

  const initialLoad = useRef(true);
  const [showRibbon, setShowRibbon] = useState(false);
  const { currentShopType } = useShopTypeContext();
  const { data, loading: alertLoading, refetch } = useAlerts(currentShopType);
  const { data: discountTimer, loading: discountLoading } =
    useSimpleDiscountTimerBar({
      shop_type: currentShopType,
    });

  const isLoading = useMemo(
    () => alertLoading || discountLoading,
    [alertLoading, discountLoading]
  );

  const { combinedAlertBars, alertsCache } = useMemo(() => {
    if (alertLoading || discountLoading || initialLoad.current) {
      return {
        combinedAlertBars: [],
        alertsCache: [],
        shouldShowRibbon: false,
      };
    }

    const alertBars = data?.alertBar?.alert;

    const combinedAlerts = [
      ...(alertBars || []),
      ...(discountTimer?.discountTimerBar?.message
        ? [discountTimer?.discountTimerBar?.message]
        : []),
    ];

    const alertsCache = combinedAlerts?.map<string>((item) =>
      Buffer.from(JSON.stringify(item)).toString("base64")
    );

    return { combinedAlertBars: combinedAlerts, alertsCache };
  }, [
    alertLoading,
    discountLoading,
    data?.alertBar?.alert,
    discountTimer?.discountTimerBar?.message,
  ]);

  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }

    if (isLoading) {
      return;
    }

    const hasNewAlerts = compareAlerts({
      gender: currentShopType,
      alerts: alertsCache,
    });

    setShowRibbon(!hasNewAlerts);
  }, [currentShopType, alertsCache, isLoading]);

  useEffect(() => {
    if (isLoggedIn) {
      void refetch();
    }
  }, [isLoggedIn, refetch]);

  const toggleShowRibbon = useCallback(() => {
    const storedAlerts =
      getStorageJSON<Record<string, string[]>>(RIBBON_STORAGE_KEY) || {};

    setStorageJSON<Record<string, string[]>>(RIBBON_STORAGE_KEY, {
      ...storedAlerts,
      [currentShopType]: alertsCache,
    });

    setShowRibbon(false);
  }, [alertsCache, currentShopType]);

  return {
    showRibbon,
    toggleShowRibbon,
    combinedAlertBars,
    discountMessage: discountTimer?.discountTimerBar?.message as AlertBar,
  };
}

export { useRibbon };
